"use client"

import { Check, ChevronsUpDown, Building2 } from "lucide-react"
import { useState, useEffect } from "react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useAuth } from "@/providers/auth-provider"
import { useCompany } from "@/hooks/use-company"
import type { Company, CompanyContext } from "@/lib/types"

export function CompanySwitcher() {
  const { user } = useAuth()
  const { switchCompany } = useCompany()
  const [open, setOpen] = useState(false)
  const [companies, setCompanies] = useState<Company[]>([])
  const [currentCompany, setCurrentCompany] = useState<CompanyContext | null>(null)

  // Load companies and current company from localStorage on mount
  useEffect(() => {
    const storedCompanies = localStorage.getItem('user_companies')
    const storedCurrentCompany = localStorage.getItem('current_company')
    
    if (storedCompanies) {
      try {
        const parsedCompanies = JSON.parse(storedCompanies)
        setCompanies(parsedCompanies)
      } catch (error) {
        console.error('Failed to parse stored companies:', error)
      }
    } else if (user?.companies) {
      // Fall back to user data if not in localStorage
      setCompanies(user.companies)
    }
    
    if (storedCurrentCompany) {
      try {
        setCurrentCompany(JSON.parse(storedCurrentCompany))
      } catch (error) {
        console.error('Failed to parse current company:', error)
      }
    } else if (user?.current_company) {
      // Fall back to user data if not in localStorage
      setCurrentCompany(user.current_company)
    }
  }, [user])

  const handleCompanySelect = (company: Company) => {
    // Create CompanyContext from selected company
    const companyContext: CompanyContext = {
      company_id: company.company_id,
      company_name: company.company_name,
      timezone: user?.current_company?.timezone || 'UTC', // Use current timezone as default
      role: company.role,
      apps: company.apps || []
    }
    
    // Clear selected app - it will be reset when the page reloads
    localStorage.removeItem('selected_app_id')
    
    // If the company has apps, select the first one by default
    if (company.apps && company.apps.length > 0) {
      localStorage.setItem('selected_app_id', company.apps[0].app_id)
    }
    
    // Close dropdown
    setOpen(false)
    
    // Use the switchCompany function from the hook which handles the redirect
    switchCompany(companyContext)
  }

  // Don't show if user only has one company or no companies
  if (companies.length <= 1) {
    return null
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className="truncate">
              {currentCompany?.company_name || "Select company..."}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder="Search companies..." />
          <CommandList>
            <CommandEmpty>No companies found.</CommandEmpty>
            <CommandGroup>
              {companies.map((company) => (
                <CommandItem
                  key={company.company_id}
                  value={company.company_name}
                  onSelect={() => handleCompanySelect(company)}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      currentCompany?.company_id === company.company_id
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                  />
                  <div className="flex flex-col">
                    <span>{company.company_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {company.role} • {company.apps_count || company.apps?.length || 0} apps
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}